import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import React from "react";
import { View, SectionList, TouchableOpacity, SafeAreaView } from "react-native";
import tw from "twrnc";

import { getCategoryIcon } from "../../constants/categoryIcons";
import { DSText } from "../../design-system/components/DSText";
import { spacing, radius, shadows } from "../../design-system/tokens";
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { t } from "../../i18n/translations";
import { RootStackParamList } from "../../navigation/types";

import { iqesColors } from "./iqesDesignSystem";

const shellColors = {
  canvas: iqesColors.canvas,
  panel: 'rgba(8,10,24,0.94)',
  panelRaised: 'rgba(16,12,34,0.96)',
  border: 'rgba(246,190,58,0.34)',
  borderStrong: 'rgba(246,190,58,0.52)',
  gold: iqesColors.gold,
  goldBright: iqesColors.goldBright,
  goldSoft: iqesColors.goldSoft,
  ivory: iqesColors.ivory,
  muted: 'rgba(255,248,232,0.72)',
};

const showDebugModeBadge = false;

// Helper function to get Bible book translation key
const getBibleBookKey = (bookName: string): string => {
  return bookName
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/^(\d+)_/, '$1_'); // Handle numbered books like "1 Samuel" -> "1_samuel"
};

// Helper function to get translated book name
const getTranslatedBookName = (bookName: string, language: string): string => {
  const key = getBibleBookKey(bookName);
  const translatedName = t(`bible_books.${key}`, language);
  return translatedName && translatedName !== `bible_books.${key}` ? translatedName : bookName;
};

const BOOKS = [
  // Old Testament
  { id: 12, name: "Genesis", testament: "old" },
  { id: 13, name: "Exodus", testament: "old" },
  { id: 14, name: "Leviticus", testament: "old" },
  { id: 15, name: "Numbers", testament: "old" },
  { id: 16, name: "Deuteronomy", testament: "old" },
  { id: 17, name: "Joshua", testament: "old" },
  { id: 18, name: "Judges", testament: "old" },
  { id: 19, name: "Ruth", testament: "old" },
  { id: 20, name: "1 Samuel", testament: "old" },
  { id: 21, name: "2 Samuel", testament: "old" },
  { id: 22, name: "1 Kings", testament: "old" },
  { id: 23, name: "2 Kings", testament: "old" },
  { id: 24, name: "1 Chronicles", testament: "old" },
  { id: 25, name: "2 Chronicles", testament: "old" },
  { id: 26, name: "Ezra", testament: "old" },
  { id: 27, name: "Nehemiah", testament: "old" },
  { id: 28, name: "Esther", testament: "old" },
  { id: 29, name: "Job", testament: "old" },
  { id: 30, name: "Psalms", testament: "old" },
  { id: 31, name: "Proverbs", testament: "old" },
  { id: 32, name: "Ecclesiastes", testament: "old" },
  { id: 33, name: "Song of Solomon", testament: "old" },
  { id: 34, name: "Isaiah", testament: "old" },
  { id: 35, name: "Jeremiah", testament: "old" },
  { id: 36, name: "Lamentations", testament: "old" },
  { id: 37, name: "Ezekiel", testament: "old" },
  { id: 38, name: "Daniel", testament: "old" },
  { id: 39, name: "Hosea", testament: "old" },
  { id: 40, name: "Joel", testament: "old" },
  { id: 41, name: "Amos", testament: "old" },
  { id: 42, name: "Obadiah", testament: "old" },
  { id: 43, name: "Jonah", testament: "old" },
  { id: 44, name: "Micah", testament: "old" },
  { id: 45, name: "Nahum", testament: "old" },
  { id: 46, name: "Habakkuk", testament: "old" },
  { id: 47, name: "Zephaniah", testament: "old" },
  { id: 48, name: "Haggai", testament: "old" },
  { id: 49, name: "Zechariah", testament: "old" },
  { id: 50, name: "Malachi", testament: "old" },
  // New Testament
  { id: 51, name: "Matthew", testament: "new" },
  { id: 52, name: "Mark", testament: "new" },
  { id: 53, name: "Luke", testament: "new" },
  { id: 54, name: "John", testament: "new" },
  { id: 55, name: "Acts", testament: "new" },
  { id: 56, name: "Romans", testament: "new" },
  { id: 57, name: "1 Corinthians", testament: "new" },
  { id: 58, name: "2 Corinthians", testament: "new" },
  { id: 59, name: "Galatians", testament: "new" },
  { id: 60, name: "Ephesians", testament: "new" },
  { id: 61, name: "Philippians", testament: "new" },
  { id: 62, name: "Colossians", testament: "new" },
  { id: 63, name: "1 Thessalonians", testament: "new" },
  { id: 64, name: "2 Thessalonians", testament: "new" },
  { id: 65, name: "1 Timothy", testament: "new" },
  { id: 66, name: "2 Timothy", testament: "new" },
  { id: 67, name: "Titus", testament: "new" },
  { id: 68, name: "Philemon", testament: "new" },
  { id: 69, name: "Hebrews", testament: "new" },
  { id: 70, name: "James", testament: "new" },
  { id: 71, name: "1 Peter", testament: "new" },
  { id: 72, name: "2 Peter", testament: "new" },
  { id: 73, name: "1 John", testament: "new" },
  { id: 74, name: "2 John", testament: "new" },
  { id: 75, name: "3 John", testament: "new" },
  { id: 76, name: "Jude", testament: "new" },
  { id: 77, name: "Revelation", testament: "new" }
];

type UnifiedBookSelectionScreenRouteProp = RouteProp<
  RootStackParamList,
  "BookSelection" | "MultiplayerBookSelection" | "ChallengeBookSelection"
>;

type UnifiedBookSelectionScreenNavigationProp = StackNavigationProp<
  RootStackParamList,
  "BookSelection" | "MultiplayerBookSelection" | "ChallengeBookSelection"
>;

type UnifiedBookSelectionScreenProps = {
  mode?: 'solo' | 'multiplayer' | 'challenge';
  testament?: "old" | "new";
  category?: string;
  categoryId?: number;
  onBookSelect?: (book: {
    id: number;
    name: string;
    testament: string;
  }) => void;
  challengeMode?: boolean;
  rematch?: boolean;
  oldRoomId?: string;
  opponent?: { id: string; name: string }; // For Challenge mode
};

// Enhanced book metadata for better UX
const getBookInfo = (bookName: string) => {
  const bookData: { [key: string]: { chapters: number; themeKey: string } } = {
    'Genesis': { chapters: 50, themeKey: 'creation_origins' },
    'Exodus': { chapters: 40, themeKey: 'freedom_law' },
    'Leviticus': { chapters: 27, themeKey: 'holiness_worship' },
    'Numbers': { chapters: 36, themeKey: 'journey_faith' },
    'Deuteronomy': { chapters: 34, themeKey: 'covenant_law' },
    'Joshua': { chapters: 24, themeKey: 'conquest_victory' },
    'Judges': { chapters: 21, themeKey: 'leadership_cycles' },
    'Ruth': { chapters: 4, themeKey: 'love_loyalty' },
    '1 Samuel': { chapters: 31, themeKey: 'kingdom_leadership' },
    '2 Samuel': { chapters: 24, themeKey: 'david_reign' },
    '1 Kings': { chapters: 22, themeKey: 'wisdom_division' },
    '2 Kings': { chapters: 25, themeKey: 'prophets_exile' },
    '1 Chronicles': { chapters: 29, themeKey: 'history_worship' },
    '2 Chronicles': { chapters: 36, themeKey: 'temple_reform' },
    'Ezra': { chapters: 10, themeKey: 'return_rebuild' },
    'Nehemiah': { chapters: 13, themeKey: 'walls_revival' },
    'Esther': { chapters: 10, themeKey: 'providence_courage' },
    'Job': { chapters: 42, themeKey: 'suffering_faith' },
    'Psalms': { chapters: 150, themeKey: 'worship_prayer' },
    'Proverbs': { chapters: 31, themeKey: 'wisdom_life' },
    'Ecclesiastes': { chapters: 12, themeKey: 'meaning_purpose' },
    'Song of Solomon': { chapters: 8, themeKey: 'love_beauty' },
    'Isaiah': { chapters: 66, themeKey: 'prophecy_hope' },
    'Jeremiah': { chapters: 52, themeKey: 'warning_restoration' },
    'Lamentations': { chapters: 5, themeKey: 'grief_hope' },
    'Ezekiel': { chapters: 48, themeKey: 'vision_glory' },
    'Daniel': { chapters: 12, themeKey: 'dreams_prophecy' },
    'Hosea': { chapters: 14, themeKey: 'love_faithfulness' },
    'Joel': { chapters: 3, themeKey: 'judgment_restoration' },
    'Amos': { chapters: 9, themeKey: 'justice_righteousness' },
    'Obadiah': { chapters: 1, themeKey: 'pride_judgment' },
    'Jonah': { chapters: 4, themeKey: 'mercy_mission' },
    'Micah': { chapters: 7, themeKey: 'justice_mercy' },
    'Nahum': { chapters: 3, themeKey: 'judgment_comfort' },
    'Habakkuk': { chapters: 3, themeKey: 'faith_trust' },
    'Zephaniah': { chapters: 3, themeKey: 'judgment_joy' },
    'Haggai': { chapters: 2, themeKey: 'rebuild_renew' },
    'Zechariah': { chapters: 14, themeKey: 'vision_messiah' },
    'Malachi': { chapters: 4, themeKey: 'return_prepare' },
    'Matthew': { chapters: 28, themeKey: 'king_kingdom' },
    'Mark': { chapters: 16, themeKey: 'servant_action' },
    'Luke': { chapters: 24, themeKey: 'savior_compassion' },
    'John': { chapters: 21, themeKey: 'son_god_love' },
    'Acts': { chapters: 28, themeKey: 'church_mission' },
    'Romans': { chapters: 16, themeKey: 'gospel_grace' },
    '1 Corinthians': { chapters: 16, themeKey: 'church_unity' },
    '2 Corinthians': { chapters: 13, themeKey: 'ministry_comfort' },
    'Galatians': { chapters: 6, themeKey: 'freedom_spirit' },
    'Ephesians': { chapters: 6, themeKey: 'unity_armor' },
    'Philippians': { chapters: 4, themeKey: 'joy_peace' },
    'Colossians': { chapters: 4, themeKey: 'christ_supremacy' },
    '1 Thessalonians': { chapters: 5, themeKey: 'hope_return' },
    '2 Thessalonians': { chapters: 3, themeKey: 'patience_work' },
    '1 Timothy': { chapters: 6, themeKey: 'leadership_doctrine' },
    '2 Timothy': { chapters: 4, themeKey: 'endurance_legacy' },
    'Titus': { chapters: 3, themeKey: 'good_works_grace' },
    'Philemon': { chapters: 1, themeKey: 'forgiveness_love' },
    'Hebrews': { chapters: 13, themeKey: 'better_perfect' },
    'James': { chapters: 5, themeKey: 'faith_works' },
    '1 Peter': { chapters: 5, themeKey: 'hope_suffering' },
    '2 Peter': { chapters: 3, themeKey: 'growth_return' },
    '1 John': { chapters: 5, themeKey: 'love_life' },
    '2 John': { chapters: 1, themeKey: 'truth_love' },
    '3 John': { chapters: 1, themeKey: 'hospitality_truth' },
    'Jude': { chapters: 1, themeKey: 'contend_keep' },
    'Revelation': { chapters: 22, themeKey: 'victory_glory' },
  };
  return bookData[bookName] || { chapters: 0, themeKey: 'scripture_study' };
};

export default function UnifiedBookSelectionScreen(props: UnifiedBookSelectionScreenProps = {}) {
  const navigation = useNavigation<UnifiedBookSelectionScreenNavigationProp>();
  const route = useRoute<UnifiedBookSelectionScreenRouteProp>();
  const { language: userLang } = useLanguageStore();

  // Mode detection: explicit prop > route name analysis > default to 'solo'
  const mode = props.mode ||
    (route.name === 'MultiplayerBookSelection' ? 'multiplayer' :
     route.name === 'ChallengeBookSelection' ? 'challenge' : 'solo');

  // Determine testament and category from props or route
  const testament = (props.testament ?? route.params?.testament) as "old" | "new" | undefined;
  const _category = props.category ?? route.params?.category;
  const _categoryId = props.categoryId ?? route.params?.categoryId;
  const challengeMode = props.challengeMode ?? (route.params as any)?.challengeMode;
  const rematch = props.rematch ?? (route.params as any)?.rematch;
  const oldRoomId = props.oldRoomId ?? (route.params as any)?.oldRoomId;
  const opponent = props.opponent ?? (route.params as any)?.opponent;
  const familyId = (route.params as any)?.familyId;
  const familyName = (route.params as any)?.familyName;
  const friendsGroupId = (route.params as any)?.friendsGroupId;
  const friendsGroupName = (route.params as any)?.friendsGroupName;

  // Only show books from the selected testament
  function chunkArray<T>(array: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }

  if (!testament) {
    return (
      <View style={[tw`flex-1`, { backgroundColor: shellColors.canvas }]}>
        <SafeAreaView style={tw`flex-1 justify-center items-center px-5`}>
          <DSText
            variant="body"
            color={shellColors.muted}
            align="center"
            style={{ marginBottom: spacing.lg, fontWeight: '600' }}
          >
            {t('no_testament_error', userLang)}
          </DSText>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={{
              backgroundColor: shellColors.goldSoft,
              paddingHorizontal: spacing['2xl'],
              paddingVertical: spacing.md,
              borderRadius: radius.lg,
              borderWidth: 1,
              borderColor: shellColors.borderStrong,
              ...shadows.md,
            }}
          >
            <DSText variant="label" color={iqesColors.navy} style={{ fontWeight: '800' }}>
              {t('back', userLang)}
            </DSText>
          </TouchableOpacity>
        </SafeAreaView>
      </View>
    );
  }

  const selectedTestament = testament === 'old' ? 'old' : 'new';
  const sections = [
    {
      title: selectedTestament === 'old'
        ? t('old_testament', userLang)
        : t('new_testament', userLang),
      data: chunkArray(BOOKS.filter((b) => b.testament === selectedTestament), 2)
    }
  ];

  const FAVORITE_BOOK_IDS = [19, 30, 51]; // Example: Ruth, Psalms, Matthew

  const handleBookSelect = (book: { id: number; name: string; testament: string }) => {
    if (mode === 'multiplayer') {
      // Multiplayer navigation
      navigation.navigate("MultiplayerDifficulty", {
        category: book.name,
        categoryId: book.id,
        mode: "books",
        rematch,
        oldRoomId,
        familyId,
        familyName,
        friendsGroupId,
        friendsGroupName,
      });
    } else if (mode === 'challenge') {
      // Challenge navigation
      navigation.navigate("ChallengeDifficulty", {
        opponent: opponent,
        categoryId: book.id,
        category: book.name,
        testament: testament,
      });
    } else {
      // Solo navigation
      navigation.navigate("Difficulty", {
        category: book.name,
        categoryId: book.id,
        testament: testament,
        challengeMode
      });
    }
  };

  // Adaptive layout based on mode
  const layoutStyle = mode === 'solo'
    ? tw`flex-1`
    : { flex: 1 };

  return (
    <View style={[tw`flex-1`, { backgroundColor: shellColors.canvas }]}>

      <SafeAreaView style={layoutStyle}>
        {/* Premium Header Section */}
        <View style={{
          alignItems: 'center',
          paddingTop: spacing.xl,
          paddingBottom: spacing['2xl'],
          paddingHorizontal: spacing['2xl'],
          borderBottomWidth: 1,
          borderBottomColor: shellColors.border,
        }}>
          {/* Back Button - only show in solo mode */}
          {mode === 'solo' && (
            <View style={{
              position: 'absolute',
              left: spacing['2xl'],
              top: spacing.xl,
              zIndex: 10,
            }}>
              <TouchableOpacity
                onPress={() => navigation.goBack()}
                style={{
                  width: spacing['4xl'],
                  height: spacing['4xl'],
                  borderRadius: radius.full,
                  backgroundColor: shellColors.panel,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 1,
                  borderColor: shellColors.border,
                }}
              >
                <Ionicons name="arrow-back" size={20} color={shellColors.goldBright} />
              </TouchableOpacity>
            </View>
          )}

          <View style={{
            backgroundColor: 'rgba(246,190,58,0.14)',
            paddingHorizontal: spacing.lg + 2,
            paddingVertical: spacing.sm + 2,
            borderRadius: radius.full,
            marginBottom: spacing.lg,
            borderWidth: 1,
            borderColor: shellColors.borderStrong,
          }}>
            <DSText
              variant={mode === 'multiplayer' ? 'title' : 'heading'}
              color={shellColors.goldSoft}
              align="center"
              style={{
                fontWeight: '800',
                letterSpacing: 0.5,
              }}
            >
              {selectedTestament === 'old'
                ? t('old_testament_display', userLang)
                : t('new_testament_display', userLang)
              }
            </DSText>
          </View>

          <DSText
            variant={mode === 'multiplayer' ? 'label' : 'body'}
            color={shellColors.muted}
            align="center"
            style={{
              fontWeight: '500',
              lineHeight: 22,
              maxWidth: 320,
            }}
          >
            {selectedTestament === 'old'
              ? t('hebrew_scriptures', userLang)
              : t('christian_scriptures', userLang)
            }
          </DSText>

          {/* Mode indicator for development */}
          {showDebugModeBadge && (
            <View style={{
              marginTop: spacing.sm,
              backgroundColor: shellColors.panel,
              borderWidth: 1,
              borderColor: shellColors.border,
              paddingHorizontal: spacing.sm,
              paddingVertical: 2,
              borderRadius: 12,
              alignSelf: 'center',
            }}>
              <DSText
                variant="micro"
                color={shellColors.muted}
                align="center"
                style={{ fontWeight: '600' }}
              >
                {mode.toUpperCase()}
              </DSText>
            </View>
          )}
        </View>

        {/* Premium Content Container */}
        <SectionList
          sections={sections}
          keyExtractor={(_, index) => index.toString()}
          contentContainerStyle={{
            paddingHorizontal: spacing.xl,
            paddingVertical: spacing['2xl'],
            paddingBottom: spacing['4xl'],
          }}
          renderItem={({ item: row, index: rowIndex }) => (
            <View style={{
              flexDirection: 'row',
              width: '100%',
              marginBottom: spacing.lg,
              gap: spacing.md,
              ...(mode === 'multiplayer' && {
                maxWidth: 600,
                alignSelf: 'center' as const,
              })
            }} key={rowIndex}>
              {row.map((item) => {
                const bookInfo = getBookInfo(item.name);
                const isPopular = FAVORITE_BOOK_IDS.includes(item.id);
                const categoryIcon = getCategoryIcon(item.id);
                const iconGradient = categoryIcon.gradientColors;
                const iconColor = iconGradient[0];

                return (
                  <TouchableOpacity
                    key={item.id}
                    onPress={() => handleBookSelect(item)}
                    style={{
                      flex: 1,
                      backgroundColor: isPopular
                        ? 'rgba(246,190,58,0.18)'
                        : shellColors.panel,
                      borderWidth: 2,
                      borderColor: isPopular
                        ? shellColors.goldBright
                        : shellColors.border,
                      borderRadius: radius.xl,
                      paddingVertical: spacing.lg + 2,
                      paddingHorizontal: spacing.lg - 2,
                      ...(isPopular ? shadows.lg : shadows.md),
                      minHeight: 120,
                      justifyContent: 'space-between',
                    }}
                    accessibilityLabel={getTranslatedBookName(item.name, userLang)}
                    accessibilityRole="button"
                    accessibilityHint={t('select_book_hint', userLang)}
                    activeOpacity={0.85}
                  >
                    {/* Header with Icon and Popular Badge */}
                    <View style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: spacing.sm,
                    }}>
                      {/* Enhanced Book Icon */}
                      <View style={{
                        width: spacing['4xl'],
                        height: spacing['4xl'],
                        borderRadius: radius.md,
                        backgroundColor: isPopular
                          ? 'rgba(246,190,58,0.18)'
                          : iconColor + '20',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderWidth: 1,
                        borderColor: isPopular
                          ? shellColors.borderStrong
                          : iconColor + '30',
                      }}>
                        <Ionicons
                          name={isPopular ? 'star' : categoryIcon.icon}
                          size={20}
                          color={isPopular ? shellColors.goldSoft : iconColor}
                        />
                      </View>

                      {/* Popular Badge */}
                      {isPopular && (
                        <View style={{
                          backgroundColor: shellColors.gold,
                          paddingHorizontal: spacing.sm,
                          paddingVertical: 3,
                          borderRadius: radius.sm + 2,
                        }}>
                          <DSText
                            variant="micro"
                            color={iqesColors.navy}
                            uppercase
                            style={{ fontWeight: '700', letterSpacing: 0.5 }}
                          >
                            {mode === 'multiplayer' ? 'Popular' : t('popular', userLang)}
                          </DSText>
                        </View>
                      )}
                    </View>

                    {/* Book Name */}
                    <DSText
                      variant="label"
                      color={shellColors.ivory}
                      style={{
                        fontWeight: '800',
                        fontSize: 17,
                        lineHeight: 20,
                        marginBottom: spacing.xs + 2,
                      }}
                    >
                      {mode === 'multiplayer'
                        ? (t(item.name, userLang) || item.name)
                        : getTranslatedBookName(item.name, userLang)
                      }
                    </DSText>

                    {/* Book Theme */}
                    <DSText
                      variant="caption"
                      color={isPopular ? shellColors.goldSoft : shellColors.muted}
                      style={{
                        fontWeight: '600',
                        marginBottom: spacing.sm,
                        lineHeight: 14,
                      }}
                    >
                      {mode === 'multiplayer'
                        ? bookInfo.themeKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                        : t(bookInfo.themeKey, userLang)
                      }
                    </DSText>

                    {/* Book Stats */}
                    <View style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <View style={{
                        backgroundColor: isPopular
                          ? 'rgba(246,190,58,0.16)'
                          : 'rgba(255,248,232,0.08)',
                        paddingHorizontal: spacing.sm,
                        paddingVertical: spacing.xs,
                        borderRadius: radius.sm,
                      }}>
                        <DSText
                          variant="micro"
                            color={isPopular ? shellColors.ivory : shellColors.muted}
                          style={{ fontWeight: '600', fontSize: 11 }}
                        >
                          {bookInfo.chapters} {mode === 'multiplayer' ? 'chapters' : t('chapters', userLang)}
                        </DSText>
                      </View>

                      {/* Arrow Indicator */}
                      <View style={{
                        width: spacing['2xl'],
                        height: spacing['2xl'],
                        borderRadius: radius.full,
                        backgroundColor: isPopular
                          ? 'rgba(246,190,58,0.16)'
                          : iconColor + '20',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Ionicons
                          name="chevron-forward"
                          size={14}
                          color={isPopular ? shellColors.goldSoft : iconColor}
                        />
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
              {/* Add empty space if odd number of books */}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          )}
          showsVerticalScrollIndicator={false}
        />
      </SafeAreaView>
    </View>
  );
}
